import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import "./style.scss";
import AutoTurnOnModal from "../pauseClass/PauseClass";
import { pauseClass, updateClassStatus, getClasslist, getClassDetails } from "../../../services/classes.service";
import { toast } from "react-toastify";
import CopyTextIcon from "../../../icons/CopyTextIcon";
import moment from "moment";
import { to12h } from "../../../utils";

export default function ClassesCard({
  className,
  image,
  status,
  classType,
  participants,
  showImg,
  classId,
  isPaused,
  maxBooking,
  handleClose,
  classDetails,
  classStatus,
}: any) {
  const navigate = useNavigate();
  const [isToggled, setIsToggled] = useState(false);
  const [isActive, setIsActive] = useState(classStatus);
  const fileUrl = process.env.REACT_APP_FILE_URL;

  const serviceName = classDetails?.services?.name;
  const startTimeRaw = classDetails?.classTime?.start || '';
  const endTimeRaw = classDetails?.classTime?.end || '';
  const weekDays = classDetails?.classRepeat?.weekDays || [];
  const bookingCount = classDetails?.classBookingCount || 0;
  const isDayPass = classDetails?.isDayPass;
  const classImage = classDetails?.images?.[0];

  const startTime = to12h(startTimeRaw);
  const endTime = to12h(endTimeRaw);

  const formatDays = (days: string[]) => {
    if (!days || days.length === 0) return '';
    if (days.length === 7 || (days.length === 1 && days[0].toLowerCase() === 'everyday')) return 'Daily';
    const abbrev: Record<string, string> = {
      monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed',
      thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun'
    };
    return days.map(d => abbrev[d.toLowerCase()] || d).join(', ');
  };

  const getStatusLabel = () => {
    if (isPaused) return 'paused';
    if (isActive) return 'active';
    return 'inactive';
  };

  const generateUniqueName = async (originalName: string): Promise<string> => {
    const locationId = localStorage.getItem('locationId') || '';
    try {
      const result = await getClasslist({ locationId, limit: 1000, skip: 0 });
      const existingNames = (result?.data?.data || []).map((c: any) => c.className?.toLowerCase());
      let baseName = `Copy of ${originalName}`;
      let candidateName = baseName;
      let counter = 2;
      while (existingNames.includes(candidateName.toLowerCase())) {
        candidateName = `${baseName} (${counter})`;
        counter++;
      }
      return candidateName;
    } catch (error) {
      return `Copy of ${originalName}`;
    }
  };

  const handleDuplicate = async () => {
    if (!classId) {
      toast.error('Unable to duplicate class.');
      return;
    }
    try {
      toast.info('Loading class details...');
      const classResult = await getClassDetails(classId);
      if (!classResult?.data?.data) {
        toast.error('Failed to load class details.');
        return;
      }
      const fullClassDetails = classResult.data.data;
      const uniqueName = await generateUniqueName(fullClassDetails.className || 'Class');
      let startDate = fullClassDetails?.classTime?.date;
      if (startDate && moment(startDate).isBefore(moment(), 'day')) {
        startDate = moment().format('YYYY-MM-DD');
      }
      const duplicatedData = {
        ...fullClassDetails,
        className: uniqueName,
        classTime: { ...fullClassDetails.classTime, date: startDate || moment().format('YYYY-MM-DD') },
        _id: undefined,
      };
      navigate('/classes/create', { state: { duplicateData: duplicatedData } });
      toast.success('Class data loaded. Modify as needed and save.');
    } catch (error) {
      toast.error('Failed to duplicate class.');
    }
  };

  const handleToggle = () => {
    if (isPaused) {
      resetClass();
    } else {
      setIsToggled(!isToggled);
    }
  };

  const resetClass = async () => {
    const obj = { type: "none", date: null };
    const result = await pauseClass(classId, { classPause: obj });
    if (result.data) {
      toast.success("Class updated successfully");
      handleClose();
    }
  };

  const handleStatusChange = async (newStatus: boolean) => {
    try {
      setIsActive(newStatus);
      const result = await updateClassStatus(classId, { classStatus: newStatus });
      if (result.data) {
        toast.success('Class status updated');
        handleClose();
      } else {
        throw new Error("Failed");
      }
    } catch (error) {
      toast.error("Error updating class status");
      setIsActive((prev: any) => !prev);
    }
  };

  useEffect(() => {
    setIsActive(classStatus);
  }, [isPaused, classStatus]);

  const handleCloseModal = () => {
    setIsToggled(false);
    handleClose();
  };

  const statusKey = getStatusLabel();

  return (
    <div className="class-card">
      {/* Image or gradient header */}
      <div className="class-card__header">
        {classImage ? (
          <img src={`${fileUrl}${classImage}`} alt={className} className="class-card__image" />
        ) : (
          <div className="class-card__image-placeholder" />
        )}
        <div className="class-card__header-overlay">
          <span className={`class-card__status class-card__status--${statusKey}`}>
            {statusKey}
          </span>
          {isDayPass && <span className="class-card__badge">Day Pass</span>}
        </div>
      </div>

      {/* Body */}
      <div className="class-card__body">
        <Link to={`/classes/detail/${classId}`} className="class-card__title">
          {className}
        </Link>

        {serviceName && (
          <span className="class-card__service">{serviceName}</span>
        )}

        {/* Time + Days */}
        <div className="class-card__meta">
          {startTime && endTime && (
            <div className="class-card__meta-row">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
              <span>{startTime} – {endTime}</span>
            </div>
          )}
          {classDetails?.classRepeat?.type === 'doesNotRepeat' && classDetails?.classTime?.date ? (
            <div className="class-card__meta-row">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <span>{moment(classDetails.classTime.date).format('MMM D, YYYY')}</span>
            </div>
          ) : weekDays.length > 0 && (
            <div className="class-card__meta-row">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              <span>{formatDays(weekDays)}</span>
            </div>
          )}
          {classDetails?.classEnd?.type === 'on' && classDetails?.classEnd?.date && (
            <div className="class-card__meta-row">
              <span>Ends {moment(classDetails.classEnd.date).format('MMM D, YYYY')}</span>
            </div>
          )}
          {classDetails?.classEnd?.type === 'after' && classDetails?.classEnd?.NoOfOccurence && (
            <div className="class-card__meta-row">
              <span>Ends after {classDetails.classEnd.NoOfOccurence} {classDetails.classEnd.NoOfOccurence === 1 ? 'occurrence' : 'occurrences'}</span>
            </div>
          )}
        </div>

        {/* Stats row */}
        <div className="class-card__stats">
          <div className="class-card__stat">
            <span className="class-card__stat-value">{bookingCount}</span>
            <span className="class-card__stat-label">Booked</span>
          </div>
          <div className="class-card__stat-divider" />
          <div className="class-card__stat">
            <span className="class-card__stat-value">{maxBooking || '∞'}</span>
            <span className="class-card__stat-label">Capacity</span>
          </div>
          <div className="class-card__stat-divider" />
          <div className="class-card__stat">
            <span className="class-card__stat-value">{classType}</span>
            <span className="class-card__stat-label">Repeats</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="class-card__footer">
        <div className="class-card__actions">
          <button
            className={`class-card__action-btn ${!isActive && !isPaused ? 'class-card__action-btn--active' : ''}`}
            onClick={() => handleStatusChange(false)}
            disabled={!isActive && !isPaused}
          >
            Inactive
          </button>
          <button
            className={`class-card__action-btn ${isPaused ? 'class-card__action-btn--paused' : ''}`}
            onClick={() => handleToggle()}
            disabled={isPaused}
          >
            Paused
          </button>
          <button
            className={`class-card__action-btn ${isActive && !isPaused ? 'class-card__action-btn--active-on' : ''}`}
            onClick={() => handleStatusChange(true)}
            disabled={isActive && !isPaused}
          >
            Active
          </button>
        </div>

        <div className="class-card__links">
          <Link to={`/classes/detail/${classId}`} className="class-card__view-btn">
            View Details
          </Link>
          <button type="button" className="class-card__dup-btn" onClick={handleDuplicate} title="Duplicate class">
            <CopyTextIcon />
            Duplicate
          </button>
        </div>
      </div>

      {isToggled && (
        <AutoTurnOnModal
          show={isToggled}
          handleClose={handleCloseModal}
          id={classId}
          isPaused={isPaused}
          classType={classType}
          classDetails={classDetails}
        />
      )}
    </div>
  );
}
